import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { ref, onValue, push, set, update, serverTimestamp, get, increment, query, orderByChild, remove } from 'firebase/database';
import { Send, ArrowLeft, User, MoreVertical, ShieldCheck, CheckCheck, Paperclip, Smile, Image as ImageIcon, Loader2, Minus, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { encryptText, decryptText } from '../utils/encryption';
import { format, isToday, isThisYear } from 'date-fns';

const Chat = ({ currentUid, otherUserUid, otherUserName, onBack, isFloating = false, onMinimize, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatId, setChatId] = useState('');
  const [myPhoto, setMyPhoto] = useState(null);
  const [otherPhoto, setOtherPhoto] = useState(null);
  const [messageLimit, setMessageLimit] = useState(20);
  const [showMenu, setShowMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportError, setReportError] = useState('');
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [myRole, setMyRole] = useState(null);
  const [otherRole, setOtherRole] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false); // I blocked them
  const [isBlockedByOther, setIsBlockedByOther] = useState(false); // they blocked me
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const faqContainerRef = useRef(null);

  const scrollFaq = (direction) => {
    if (faqContainerRef.current) {
      const scrollAmount = direction === 'left' ? -180 : 180;
      faqContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (otherUserUid && !isFloating) {
      window.dispatchEvent(new CustomEvent('rc-chat-opened', { detail: { otherUserUid } }));
      return () => {
        window.dispatchEvent(new CustomEvent('rc-chat-closed', { detail: { otherUserUid } }));
      };
    }
  }, [otherUserUid, isFloating]);

  useEffect(() => {
    // Fetch photos
    const fetchPhotos = async () => {
      try {
        // Get my photo
        const myUserSnap = await get(ref(db, `users/${currentUid}`));
        if (myUserSnap.exists()) {
          const userData = myUserSnap.val();
          setMyRole(userData.role);
          if (userData.profilePicUrl) {
            setMyPhoto(userData.profilePicUrl);
          } else if (userData.photoURL || userData.idPhotoUrl || userData.idImageUrl) {
            setMyPhoto(userData.photoURL || userData.idPhotoUrl || userData.idImageUrl);
          } else if (userData.role === 'Owner') {
            const propSnap = await get(ref(db, `properties/${currentUid}`));
            if (propSnap.exists()) {
              const propData = propSnap.val();
              const imgs = Array.isArray(propData.imageUrls) ? propData.imageUrls : (propData.imageUrls ? Object.values(propData.imageUrls) : []);
              if (imgs.length > 0) setMyPhoto(imgs[0]);
            }
          }
        }
      } catch (e) { console.warn("My profile read failed", e); }

      try {
        // Get other user's photo
        const otherPropSnap = await get(ref(db, `properties/${otherUserUid}`));
        if (otherPropSnap.exists()) {
          const propData = otherPropSnap.val();
          const imgs = Array.isArray(propData.imageUrls) ? propData.imageUrls : (propData.imageUrls ? Object.values(propData.imageUrls) : []);
          if (imgs.length > 0) setOtherPhoto(imgs[0]);
          setOtherRole('Owner');
        } else {
          const otherUserSnap = await get(ref(db, `users/${otherUserUid}`));
          if (otherUserSnap.exists()) {
            const otherVal = otherUserSnap.val();
            setOtherRole(otherVal.role);
            const otherImg = otherVal.profilePicUrl || otherVal.photoURL || otherVal.photoUrl || otherVal.idPhotoUrl || otherVal.idImageUrl;
            if (otherImg) {
              setOtherPhoto(otherImg);
            }
          }
        }
      } catch (e) { console.warn("Other profile read failed", e); }
    };

    fetchPhotos();

    const ids = [currentUid, otherUserUid].sort();
    const id = ids.join('_');
    setChatId(id);

    const chatRef = ref(db, `chats/${id}/messages`);
    const chatQuery = query(chatRef, orderByChild('timestamp')); // Cannot limitToLast here efficiently while maintaining real-time on top.
    // We'll limit locally for better UX.

    // Reset unread count for this room when opening
    try {
      update(ref(db, `chat_rooms/${currentUid}/${otherUserUid}`), { unreadCount: 0 });
      get(ref(db, `notifications/${currentUid}`)).then(snap => {
        if (snap.exists()) {
          const updates = {};
          Object.entries(snap.val()).forEach(([notifId, n]) => {
            if (n.type === 'new_message' && n.senderUid === otherUserUid && !n.isRead) {
              updates[`notifications/${currentUid}/${notifId}/isRead`] = true;
            }
          });
          if (Object.keys(updates).length > 0) {
            update(ref(db), updates);
          }
        }
      }).catch(() => {});
    } catch (e) { console.warn("Unread reset failed", e); }

    const unsubscribe = onValue(chatQuery, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.entries(data).map(([msgId, val]) => ({
          id: msgId,
          ...val,
          decryptedText: decryptText(val.text, id)
        })).sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
        setMessages(list);
      } else {
        setMessages([]);
      }
    });

    // Check if I blocked them
    const myBlockRef = ref(db, `blocks/${currentUid}/${otherUserUid}`);
    const unsubscribeMyBlock = onValue(myBlockRef, (snap) => {
      setIsBlocked(snap.exists());
    });

    // Check if they blocked me
    const theirBlockRef = ref(db, `blocks/${otherUserUid}/${currentUid}`);
    const unsubscribeTheirBlock = onValue(theirBlockRef, (snap) => {
      setIsBlockedByOther(snap.exists());
    });

    return () => {
      unsubscribe();
      unsubscribeMyBlock();
      unsubscribeTheirBlock();
    };
  }, [currentUid, otherUserUid, messageLimit]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const insertEmoji = (emoji) => {
    setNewMessage(prev => prev + emoji);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Only image attachments are currently supported.');
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      alert('File size exceeds 8MB limit.');
      return;
    }

    try {
      setIsUploadingAttachment(true);
      const data = new FormData();
      data.append('file', file);
      data.append('upload_preset', 'resort_unsigned');

      const response = await fetch('https://api.cloudinary.com/v1_1/dnv6ezitm/image/upload', {
        method: 'POST',
        body: data,
      });

      const resJson = await response.json();
      if (!resJson.secure_url) {
        throw new Error(resJson.error?.message || 'Failed to upload image.');
      }

      const uploadedUrl = resJson.secure_url;
      const captionText = newMessage.trim() || 'Sent an image attachment';
      const encrypted = encryptText(captionText, chatId);
      const messagesRef = ref(db, `chats/${chatId}/messages`);

      await push(messagesRef, {
        senderUid: currentUid,
        text: encrypted,
        imageUrl: uploadedUrl,
        timestamp: serverTimestamp(),
        seen: false,
      });

      updateChatRoom(currentUid, otherUserUid, otherUserName, `[Photo] ${captionText}`, otherPhoto, false);

      let myName = "User";
      try {
        const myProfileSnap = await get(ref(db, `users/${currentUid}`));
        if (myProfileSnap.exists()) {
          const val = myProfileSnap.val();
          myName = `${val.firstName || ''} ${val.lastName || ''}`.trim() || "User";
        }
      } catch (err) {}

      updateChatRoom(otherUserUid, currentUid, myName, `[Photo] ${captionText}`, myPhoto, true);

      // Notification
      try {
        await push(ref(db, `notifications/${otherUserUid}`), {
          title: `New Photo from ${myName}`,
          message: captionText,
          type: 'new_message',
          senderUid: currentUid,
          chatId: chatId,
          isRead: false,
          isArchived: false,
          timestamp: serverTimestamp()
        });
      } catch (err) {}

      setNewMessage('');
    } catch (err) {
      console.error('Attachment upload failed:', err);
      alert('Failed to send image attachment. Please check connection and try again.');
    } finally {
      setIsUploadingAttachment(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      const encrypted = encryptText(newMessage.trim(), chatId);
      const messagesRef = ref(db, `chats/${chatId}/messages`);

      await push(messagesRef, {
        senderUid: currentUid,
        text: encrypted,
        timestamp: serverTimestamp(),
        seen: false,
      });

      // Update SENDER'S room
      updateChatRoom(currentUid, otherUserUid, otherUserName, encrypted, otherPhoto, false);

      // Fetch my name for RECIPIENT'S room
      let myName = "User";
      try {
        const myProfileSnap = await get(ref(db, `users/${currentUid}`));
        if (myProfileSnap.exists()) {
          const val = myProfileSnap.val();
          myName = `${val.firstName || ''} ${val.lastName || ''}`.trim() || "User";
        } else {
          // Check if I'm an owner
          const myPropSnap = await get(ref(db, `properties/${currentUid}`));
          if (myPropSnap.exists()) {
            myName = myPropSnap.val().name || "Resort";
          }
        }
      } catch (e) {
        console.warn("Could not fetch sender profile for recipient metadata", e);
      }

      // Update RECIPIENT'S room
      updateChatRoom(otherUserUid, currentUid, myName, encrypted, myPhoto, true);

      // Create a notification for the recipient (e.g. Guest or Host)
      try {
        const notifRef = ref(db, `notifications/${otherUserUid}`);
        await push(notifRef, {
          title: `New Message from ${myName}`,
          message: newMessage.trim(),
          type: 'new_message',
          senderUid: currentUid,
          chatId: chatId,
          isRead: false,
          isArchived: false,
          timestamp: serverTimestamp()
        });
      } catch (notifErr) {
        console.warn("Could not push notification for message:", notifErr);
      }

      setNewMessage('');
    } catch (error) {
      console.error("Message send failed", error);
      alert("Failed to send message. Please check your connection.");
    }
  };

  const updateChatRoom = async (userUid, otherUid, name, lastMsg, photoUrl, incrementUnread) => {
    try {
      const roomRef = ref(db, `chat_rooms/${userUid}/${otherUid}`);

      const data = {
        otherUserName: name,
        lastMessage: lastMsg,
        timestamp: serverTimestamp()
      };
      if (photoUrl) data.otherProfilePic = photoUrl;
      if (incrementUnread) {
        data.unreadCount = increment(1);
      }

      await update(roomRef, data);
    } catch (e) {
      console.warn(`Could not update chat room metadata for ${userUid}`, e);
    }
  };

  return (
    <div
      className={`chat-interface view-transition ${isFloating ? 'floating-chat-tab' : ''}`}
      style={isFloating ? {
        display: 'flex',
        flexDirection: 'column',
        height: '470px',
        maxHeight: 'calc(100vh - 80px)',
        width: '350px',
        maxWidth: 'calc(100vw - 20px)',
        background: 'var(--surface)',
        borderRadius: '14px 14px 0 0',
        overflow: 'hidden',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.45), 0 0 0 1px var(--border)',
        border: '1px solid var(--border)',
        borderBottom: 'none',
        position: 'fixed',
        bottom: 0,
        right: '100px',
        zIndex: 9998
      } : {
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 140px)',
        background: 'var(--surface)',
        borderRadius: '32px',
        overflow: 'hidden',
        boxShadow: 'var(--shadow)',
        border: '1px solid var(--border)'
      }}
    >
      {/* Chat Header */}
      <div style={{
        padding: isFloating ? '10px 14px' : '16px 24px',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'var(--surface)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isFloating ? '10px' : '16px', minWidth: 0, flex: 1 }}>
          {onBack && !isFloating && (
            <button onClick={onBack} className="back-btn-chat">
              <ArrowLeft size={20} />
            </button>
          )}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div style={{
              width: isFloating ? '34px' : '48px',
              height: isFloating ? '34px' : '48px',
              borderRadius: isFloating ? '50%' : '16px',
              background: 'var(--light-bg)',
              overflow: 'hidden',
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              color: 'var(--text-muted)', border: '2px solid var(--border)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}>
              {otherPhoto ? (
                <img src={otherPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <User size={isFloating ? 18 : 24} />
              )}
            </div>
            <div style={{
              position: 'absolute', bottom: '-1px', right: '-1px',
              width: isFloating ? '9px' : '14px',
              height: isFloating ? '9px' : '14px',
              background: '#10B981',
              borderRadius: '50%',
              border: '2px solid var(--surface)'
            }}></div>
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h4 style={{
              margin: 0,
              fontSize: isFloating ? '13.5px' : '16px',
              fontWeight: 800,
              color: 'var(--text-main)',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{otherUserName}</span>
              <ShieldCheck size={13} color="var(--secondary)" style={{ flexShrink: 0 }} />
            </h4>
            <span style={{ fontSize: isFloating ? '10.5px' : '12px', color: 'var(--secondary)', fontWeight: 700 }}>Online</span>
          </div>
        </div>

        {/* Header Action Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
          {isFloating ? (
            <>
              {onMinimize && (
                <button
                  onClick={onMinimize}
                  title="Minimize"
                  aria-label="Minimize chat"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'var(--transition)'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--nav-group-bg)'; e.currentTarget.style.color = 'var(--text-main)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                >
                  <Minus size={18} />
                </button>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  title="Close"
                  aria-label="Close chat"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'var(--transition)'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)'; e.currentTarget.style.color = '#EF4444'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                >
                  <X size={18} />
                </button>
              )}
            </>
          ) : (
            <div style={{ position: 'relative' }}>
              <button className="icon-btn-more" onClick={() => setShowMenu(!showMenu)}>
                <MoreVertical size={20} color="var(--text-muted)" />
              </button>
              {showMenu && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '16px',
                  boxShadow: 'var(--shadow)',
                  padding: '8px',
                  zIndex: 100,
                  minWidth: '160px',
                  animation: 'fadeIn 0.2s ease-out'
                }}>
                  {!(myRole === 'Tourist' && otherRole === 'Owner') && (
                    <button className="chat-dropdown-item" onClick={() => { setShowReportModal(true); setShowMenu(false); }}>Report User</button>
                  )}
                  {myRole !== 'Tourist' && (
                    isBlocked ? (
                      <button className="chat-dropdown-item" style={{ color: '#16A34A' }} onClick={async () => {
                        setShowMenu(false);
                        try {
                          await set(ref(db, `blocks/${currentUid}/${otherUserUid}`), null);
                          alert(`${otherUserName} has been unblocked.`);
                        } catch (e) { alert('Failed to unblock. Please try again.'); }
                      }}>Unblock User</button>
                    ) : (
                      <button className="chat-dropdown-item" onClick={() => { setShowBlockConfirm(true); setShowMenu(false); }}>Block User</button>
                    )
                  )}
                  <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }}></div>
                  <button className="chat-dropdown-item" style={{ color: 'var(--primary)' }} onClick={() => { setShowClearConfirm(true); setShowMenu(false); }}>
                    Clear Chat
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: isFloating ? '14px 16px' : '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: isFloating ? '10px' : '16px',
        background: 'var(--light-bg)'
      }}>
        {messages.length > messageLimit && (
          <button
            className="btn btn-secondary"
            style={{ fontSize: '11px', padding: '8px', margin: '0 auto 20px', borderRadius: '12px' }}
            onClick={() => setMessageLimit(prev => prev + 20)}
          >
            Load Older Messages
          </button>
        )}

        {messages.length === 0 && (
           <div style={{ textAlign: 'center', margin: 'auto', opacity: 0.5 }}>
              <div style={{ background: 'var(--surface)', padding: '20px', borderRadius: '24px', display: 'inline-block', boxShadow: '0 4px 20px rgba(0,0,0,0.02)', border: '1px solid var(--border)' }}>
                 <p style={{ margin: 0, fontSize: '13px', fontWeight: 600 }}>No messages yet. Say hello!</p>
              </div>
           </div>
        )}

        {messages.slice(-messageLimit).map((msg, index) => {
          const isMe = msg.senderUid === currentUid;
          const showTime = true;

          const formatMessageTime = (ts) => {
            if (!ts) return '';
            const d = new Date(ts);
            if (isToday(d)) return format(d, 'p');
            if (isThisYear(d)) return format(d, 'MMM d, p');
            return format(d, 'MMM d, yyyy, p');
          };

          // Update seen status
          if (!isMe && msg.seen === false) {
             update(ref(db, `chats/${chatId}/messages/${msg.id}`), { seen: true });
          }

          return (
            <div key={msg.id} style={{
              alignSelf: isMe ? 'flex-end' : 'flex-start',
              maxWidth: '85%',
              display: 'flex',
              gap: '12px',
              flexDirection: isMe ? 'row-reverse' : 'row',
              alignItems: 'flex-end'
            }}>
              {/* Avatar next to message */}
              <div style={{
                width: '32px', height: '32px', borderRadius: '10px',
                background: 'var(--light-bg)', overflow: 'hidden',
                flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center',
                boxShadow: '0 2px 5px rgba(0,0,0,0.05)', marginBottom: showTime ? '18px' : '0'
              }}>
                {(isMe ? myPhoto : otherPhoto) ? (
                  <img src={isMe ? myPhoto : otherPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <User size={16} color="#9CA3AF" />
                )}
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isMe ? 'flex-end' : 'flex-start'
              }}>
                <div style={{
                  padding: msg.imageUrl ? '8px' : '12px 18px',
                  borderRadius: isMe ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                  background: isMe ? 'linear-gradient(135deg, var(--primary), #FF5F6D)' : 'var(--surface)',
                  color: isMe ? 'white' : 'var(--text-main)',
                  fontSize: '15px',
                  fontWeight: 500,
                  boxShadow: isMe ? '0 4px 15px rgba(251, 54, 64, 0.2)' : '0 2px 8px rgba(0,0,0,0.03)',
                  lineHeight: '1.5',
                  border: isMe ? 'none' : '1px solid var(--border)',
                  maxWidth: '320px'
                }}>
                  {msg.imageUrl && (
                    <div style={{ borderRadius: '14px', overflow: 'hidden', marginBottom: (msg.decryptedText && msg.decryptedText !== 'Sent an image attachment') ? '8px' : '0' }}>
                      <img
                        src={msg.imageUrl}
                        alt="Attachment"
                        style={{ width: '100%', maxHeight: '240px', objectFit: 'cover', display: 'block', cursor: 'pointer' }}
                        onClick={() => window.open(msg.imageUrl, '_blank')}
                      />
                    </div>
                  )}
                  {msg.decryptedText && (!msg.imageUrl || msg.decryptedText !== 'Sent an image attachment') && (
                    <div style={{ padding: msg.imageUrl ? '4px 8px' : '0' }}>
                      {msg.decryptedText}
                    </div>
                  )}
                </div>
                {showTime && msg.timestamp && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                    <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {formatMessageTime(msg.timestamp)}
                    </span>
                    {isMe && (
                      <CheckCheck size={14} color={msg.seen ? '#3B82F6' : '#9CA3AF'} />
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Block Status Banners */}
      {isBlocked && (
        <div style={{ padding: '10px 24px', background: 'rgba(245,158,11,0.12)', borderTop: '1px solid rgba(245,158,11,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <span style={{ fontSize: '13px', fontWeight: 700, color: '#B45309' }}>🚫 You have blocked {otherUserName}. You cannot send messages.</span>
          <button className="btn" style={{ padding: '6px 14px', fontSize: '12px', background: 'white', color: '#B45309', border: '1px solid #F59E0B', borderRadius: '10px', fontWeight: 700 }}
            onClick={async () => {
              try {
                await set(ref(db, `blocks/${currentUid}/${otherUserUid}`), null);
              } catch (e) { alert('Failed to unblock. Please try again.'); }
            }}>Unblock</button>
        </div>
      )}
      {isBlockedByOther && !isBlocked && (
        <div style={{ padding: '10px 24px', background: 'rgba(239,68,68,0.1)', borderTop: '1px solid rgba(239,68,68,0.2)' }}>
          <span style={{ fontSize: '13px', fontWeight: 700, color: '#DC2626' }}>⛔ You cannot send messages to this user.</span>
        </div>
      )}

      {/* Suggested FAQs */}
      {!isBlocked && !isBlockedByOther && myRole === 'Tourist' && (
        <div style={{
          position: 'relative',
          padding: isFloating ? '6px 8px' : '10px 16px',
          background: 'var(--surface)',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          {/* Scroll Left Button */}
          <button
            type="button"
            onClick={() => scrollFaq('left')}
            className="faq-nav-btn"
            title="Scroll FAQs left"
            aria-label="Scroll FAQs left"
          >
            <ChevronLeft size={16} />
          </button>

          {/* Scrollable Chips Container */}
          <div
            ref={faqContainerRef}
            className="faq-scroll-container"
            onWheel={(e) => {
              if (faqContainerRef.current && e.deltaY !== 0) {
                faqContainerRef.current.scrollLeft += e.deltaY;
              }
            }}
            style={{
              display: 'flex',
              gap: '6px',
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              WebkitOverflowScrolling: 'touch',
              flex: 1,
              padding: '4px 2px',
              whiteSpace: 'nowrap'
            }}
          >
            {[
              "What are the check-in and check-out times?",
              "What are the rules and guidelines?",
              "How do I reach customer support?"
            ].map((faq, idx) => (
              <button
                key={idx}
                onClick={async () => {
                  try {
                    const encryptedQ = encryptText(faq, chatId);
                    const messagesRef = ref(db, `chats/${chatId}/messages`);
                    await push(messagesRef, {
                      senderUid: currentUid,
                      text: encryptedQ,
                      timestamp: serverTimestamp(),
                      seen: true,
                    });

                    let answer = "";
                    if (faq === "What are the check-in and check-out times?") {
                      const propSnap = await get(ref(db, `properties/${otherUserUid}`));
                      if (propSnap.exists() && propSnap.val()) {
                        const propData = propSnap.val();
                        const checkIn = propData.checkInTime || '2:00 PM';
                        const checkOut = propData.checkOutTime || '12:00 PM';
                        answer = `Hello! Our standard check-in time is at ${checkIn} and check-out time is at ${checkOut}.`;
                      } else {
                        answer = `Hello! Our standard check-in time is at 2:00 PM and check-out time is at 12:00 PM. Please message the host to confirm.`;
                      }
                    } else if (faq === "What are the rules and guidelines?") {
                      const propSnap = await get(ref(db, `properties/${otherUserUid}`));
                      if (propSnap.exists() && propSnap.val() && propSnap.val().rules) {
                        answer = `Here are our rules: ${propSnap.val().rules}`;
                      } else {
                        answer = "Please treat the property with respect, keep noise levels down after 10 PM, and clean up before you leave. Message the host for specific rules.";
                      }
                    } else if (faq === "How do I reach customer support?") {
                      answer = "You can reach customer support by contacting the Admin through the 'Contact Us' page or emailing support@resortsconnect.com.";
                    }

                    setTimeout(async () => {
                      const encryptedA = encryptText(answer, chatId);
                      await push(messagesRef, {
                        senderUid: otherUserUid,
                        text: encryptedA,
                        timestamp: serverTimestamp(),
                        seen: false,
                      });
                    }, 1000);
                  } catch (e) {
                    console.error(e);
                  }
                }}
                className="faq-chip-btn"
                style={{
                  padding: isFloating ? '6px 12px' : '8px 16px',
                  borderRadius: '20px',
                  border: '1px solid var(--border)',
                  background: 'var(--light-bg)',
                  color: 'var(--text-main)',
                  fontSize: isFloating ? '12px' : '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'var(--transition)',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = 'var(--primary-soft)'; e.currentTarget.style.color = 'var(--primary)'; e.currentTarget.style.borderColor = 'var(--primary)'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = 'var(--light-bg)'; e.currentTarget.style.color = 'var(--text-main)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
              >
                {faq}
              </button>
            ))}
          </div>

          {/* Scroll Right Button */}
          <button
            type="button"
            onClick={() => scrollFaq('right')}
            className="faq-nav-btn"
            title="Scroll FAQs right"
            aria-label="Scroll FAQs right"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Input Area */}
      <div style={{ padding: '16px 24px', background: 'var(--surface)', borderTop: '1px solid var(--border)', position: 'relative' }}>
        {/* Emoticon Picker Popover */}
        {showEmojiPicker && !isBlocked && !isBlockedByOther && (
          <div style={{
            position: 'absolute',
            bottom: '90px',
            left: '24px',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '16px',
            padding: '12px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            display: 'grid',
            gridTemplateColumns: 'repeat(8, 1fr)',
            gap: '8px',
            zIndex: 100,
            maxWidth: '340px'
          }}>
            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', borderBottom: '1px solid var(--border)', paddingBottom: '4px' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Emoticons</span>
              <button
                type="button"
                onClick={() => setShowEmojiPicker(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700 }}
              >
                ✕
              </button>
            </div>
            {['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣',
              '😊', '😇', '🙂', '😉', '😍', '🥰', '😘', '😋',
              '😎', '🥳', '🤩', '👍', '👏', '🙌', '🙏', '❤️',
              '🔥', '🎉', '🏖️', '🌊', '🌴', '🏨', '⭐', '✨'].map((emoji, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => insertEmoji(emoji)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '22px',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  padding: '4px',
                  lineHeight: '1',
                  transition: 'transform 0.1s'
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.25)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={sendMessage} style={{ display: 'flex', gap: isFloating ? '6px' : '8px', alignItems: 'center' }}>
          {/* Hidden File Input for Cloudinary Attachments */}
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
            disabled={isBlocked || isBlockedByOther || isUploadingAttachment}
          />

          {/* Attachment Paperclip Button */}
          <button
            type="button"
            title="Attach image"
            disabled={isBlocked || isBlockedByOther || isUploadingAttachment}
            onClick={() => fileInputRef.current?.click()}
            style={{
              width: isFloating ? '36px' : '46px',
              height: isFloating ? '36px' : '46px',
              aspectRatio: '1 / 1',
              borderRadius: isFloating ? '12px' : '16px',
              border: isUploadingAttachment ? '1px solid var(--primary-soft)' : '1px solid var(--border)',
              background: isUploadingAttachment ? 'var(--primary-soft)' : 'var(--light-bg)',
              color: 'var(--text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: (isBlocked || isBlockedByOther || isUploadingAttachment) ? 'not-allowed' : 'pointer',
              flexShrink: 0,
              padding: 0,
              boxSizing: 'border-box',
              transition: 'var(--transition)'
            }}
            onMouseOver={(e) => { if (!isBlocked && !isBlockedByOther && !isUploadingAttachment) { e.currentTarget.style.color = 'var(--primary)'; e.currentTarget.style.background = 'var(--surface)'; } }}
            onMouseOut={(e) => { if (!isBlocked && !isBlockedByOther && !isUploadingAttachment) { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'var(--light-bg)'; } }}
          >
            {isUploadingAttachment ? (
              <span
                style={{
                  display: 'inline-block',
                  width: isFloating ? '16px' : '20px',
                  height: isFloating ? '16px' : '20px',
                  aspectRatio: '1 / 1',
                  boxSizing: 'border-box',
                  border: '2.5px solid rgba(251, 54, 64, 0.25)',
                  borderTopColor: '#FB3640',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                  transformOrigin: 'center center',
                  flexShrink: 0
                }}
              />
            ) : (
              <Paperclip size={isFloating ? 18 : 20} />
            )}
          </button>

          {/* Emoticon Picker Button */}
          <button
            type="button"
            title="Add emoticon"
            disabled={isBlocked || isBlockedByOther}
            onClick={() => setShowEmojiPicker(prev => !prev)}
            style={{
              width: isFloating ? '36px' : '46px',
              height: isFloating ? '36px' : '46px',
              borderRadius: isFloating ? '12px' : '16px',
              border: showEmojiPicker ? '1px solid var(--secondary)' : '1px solid var(--border)',
              background: showEmojiPicker ? 'rgba(29, 211, 176, 0.1)' : 'var(--light-bg)',
              color: showEmojiPicker ? 'var(--secondary)' : 'var(--text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: (isBlocked || isBlockedByOther) ? 'not-allowed' : 'pointer',
              flexShrink: 0,
              transition: 'var(--transition)'
            }}
          >
            <Smile size={isFloating ? 18 : 20} />
          </button>

          {/* Text input */}
          <div style={{ flex: 1, position: 'relative' }}>
            <input
              type="text"
              placeholder={isBlocked || isBlockedByOther ? 'Messaging is unavailable.' : (isFloating ? 'Aa' : 'Type your message...')}
              className={isFloating ? 'chat-input chat-input-floating' : 'chat-input'}
              value={newMessage}
              disabled={isBlocked || isBlockedByOther}
              onChange={(e) => setNewMessage(e.target.value)}
              style={isBlocked || isBlockedByOther ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            />
          </div>

          {/* Send Button */}
          <button
            type="submit"
            disabled={!newMessage.trim() || isBlocked || isBlockedByOther}
            style={{
              width: isFloating ? '38px' : '48px',
              height: isFloating ? '38px' : '48px',
              borderRadius: isFloating ? '12px' : '16px',
              border: '1px solid var(--border)',
              background: (newMessage.trim() && !isBlocked && !isBlockedByOther) ? 'var(--secondary)' : 'var(--light-bg)',
              color: (newMessage.trim() && !isBlocked && !isBlockedByOther) ? '#002D24' : 'var(--text-muted)',
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              cursor: (newMessage.trim() && !isBlocked && !isBlockedByOther) ? 'pointer' : 'default',
              transition: 'var(--transition)',
              boxShadow: (newMessage.trim() && !isBlocked && !isBlockedByOther) ? '0 8px 15px rgba(29, 211, 176, 0.3)' : 'none',
              flexShrink: 0
            }}
          >
            <Send size={isFloating ? 17 : 20} style={{ transform: (newMessage.trim() && !isBlocked && !isBlockedByOther) ? 'translateX(1px)' : 'none' }} />
          </button>
        </form>
      </div>

      <style>{`
        .back-btn-chat { background: var(--light-bg); border: 1px solid var(--border); width: 36px; height: 36px; border-radius: 12px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--text-main); transition: var(--transition); }
        .back-btn-chat:hover { background: var(--surface); transform: translateX(-3px); }
        .icon-btn-more { background: transparent; border: none; width: 36px; height: 36px; border-radius: 12px; display: flex; align-items: center; justify-content: center; cursor: pointer; }
        .icon-btn-more:hover { background: var(--light-bg); }
        .chat-input { width: 100%; padding: 14px 20px; border-radius: 18px; border: 2px solid var(--border); background: var(--light-bg); color: var(--text-main); font-family: inherit; font-size: 15px; font-weight: 500; outline: none; transition: var(--transition); }
        .chat-input:focus { border-color: var(--secondary); background: var(--surface); box-shadow: 0 0 0 4px rgba(29, 211, 176, 0.05); }
        .view-transition { animation: fadeIn 0.4s ease-out; }
        .chat-dropdown-item { width: 100%; text-align: left; padding: 10px 12px; background: transparent; border: none; font-size: 13px; font-weight: 600; color: var(--text-main); border-radius: 8px; cursor: pointer; transition: var(--transition); }
        .chat-dropdown-item:hover { background: var(--light-bg); }
      `}</style>

      {showReportModal && (
        <div className="modal-overlay" style={{ zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-content" style={{ background: 'var(--surface)', borderRadius: '24px', maxWidth: '400px', width: '90%', textAlign: 'center', padding: '32px 24px' }}>
            <div style={{ background: 'var(--primary-soft)', width: '64px', height: '64px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto 16px' }}>
              <ShieldCheck size={32} color="var(--primary)" />
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 800 }}>Report User</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '20px' }}>Please describe the issue with this user. This report will be sent to the super admin for review. <strong style={{color: '#EF4444'}}>Warning: False reporting may result in a warning or ban for your account.</strong></p>
            {reportError && (
              <div style={{ background: 'var(--logout-bg)', color: 'var(--primary)', padding: '10px', borderRadius: '12px', fontSize: '13px', fontWeight: 700, marginBottom: '16px', border: '1px solid var(--primary-soft)' }}>
                {reportError}
              </div>
            )}
            <textarea
              className="input"
              rows={4}
              placeholder="Reason for reporting..."
              value={reportReason}
              onChange={(e) => {
                setReportReason(e.target.value);
                if (reportError) setReportError('');
              }}
              style={{ width: '100%', marginBottom: '24px', resize: 'none' }}
            />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => {
                setShowReportModal(false);
                setReportError('');
              }}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={async () => {
                if (!reportReason.trim()) {
                  setReportError('Must input a reason for reporting user.');
                  return;
                }
                try {
                  const reportKey = `report_${Date.now()}`;
                  await set(ref(db, `reports/${reportKey}`), {
                    reportedUid: otherUserUid || 'Unknown',
                    reportedName: otherUserName || 'Unknown',
                    reporterUid: currentUid || 'Unknown',
                    reason: reportReason.trim(),
                    status: 'pending',
                    timestamp: Date.now()
                  });
                  setShowReportModal(false);
                  setReportReason('');
                  setReportError('');
                  alert('Report successfully submitted to super admin.');
                } catch (e) {
                  console.error('Report error:', e);
                  setReportError(`Failed to submit report: ${e.message || 'Permission denied. Please check connection.'}`);
                }
              }}>Submit Report</button>
            </div>
          </div>
        </div>
      )}

      {showBlockConfirm && (
        <div className="modal-overlay" style={{ zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-content" style={{ background: 'var(--surface)', borderRadius: '24px', maxWidth: '400px', width: '90%', textAlign: 'center', padding: '32px 24px' }}>
            <div style={{ background: 'rgba(245, 158, 11, 0.1)', width: '64px', height: '64px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto 16px' }}>
              <ShieldCheck size={32} color="#F59E0B" />
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 800 }}>Block User?</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '24px' }}>Are you sure you want to block the user? You will no longer receive messages from this user. This action can be undone later.</p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowBlockConfirm(false)}>Cancel</button>
              <button className="btn" style={{ flex: 1, background: '#F59E0B', color: 'white' }} onClick={async () => {
                try {
                  await set(ref(db, `blocks/${currentUid}/${otherUserUid}`), {
                    blockedAt: Date.now(),
                    blockedName: otherUserName
                  });
                  setShowBlockConfirm(false);
                  alert(`${otherUserName} has been blocked. You will no longer receive messages from this user.`);
                  if (onBack) onBack();
                } catch (e) {
                  console.error('Block error:', e);
                  alert('Failed to block user. Please try again.');
                }
              }}>Block User</button>
            </div>
          </div>
        </div>
      )}

      {showClearConfirm && (
        <div className="modal-overlay" style={{ zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-content" style={{ background: 'var(--surface)', borderRadius: '24px', maxWidth: '400px', width: '90%', textAlign: 'center', padding: '32px 24px' }}>
            <div style={{ background: 'var(--primary-soft)', width: '64px', height: '64px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto 16px' }}>
              <ShieldCheck size={32} color="var(--primary)" />
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 800 }}>Clear Chat History?</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '24px' }}>Are you sure to clear the chat history? This will permanently delete all messages in this conversation for you. This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowClearConfirm(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={async () => {
                try {
                  await remove(ref(db, `chats/${chatId}/messages`));
                  setShowClearConfirm(false);
                } catch (e) {
                  console.error(e);
                  alert('Failed to clear chat.');
                }
              }}>Clear Chat</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Chat;
